import type { Sides } from "@project/emerge-types";
import MakerJS from "makerjs";
import type * as THREE from "three";
import { RectPropertiesBySide } from "../structure/RectProperties";

const svgModelCache = new Map<string, MakerJS.IModel>();

/** Same cache/parser as {@link PanelRenderer} — SVG path → MakerJS model. */
export function getCachedSvgModel(svgPath: string): MakerJS.IModel {
  let cachedModel = svgModelCache.get(svgPath);
  if (!cachedModel) {
    cachedModel = MakerJS.importer.fromSVGPathData(svgPath);
    svgModelCache.set(svgPath, cachedModel);
  }
  return cachedModel;
}

/** Mirror Y, scale, rotate, then translate — same as PanelRenderer `transformCutoutModel`. */
export function transformCutoutModel(
  model: MakerJS.IModel,
  dx: number,
  dy: number,
  orientation: number,
): MakerJS.IModel {
  return MakerJS.model.moveRelative(
    MakerJS.model.rotate(
      MakerJS.model.scale(MakerJS.model.mirror(model, false, true), 1, true),
      orientation,
      [0, 0],
    ),
    [dx, dy],
  );
}

export function computeCutoutDxDy(
  cutoutPosition: { x: number; y: number; z: number },
  size: THREE.Vector2,
  panelPosition: THREE.Vector3,
  rectProps: (typeof RectPropertiesBySide)[Sides],
): { dx: number; dy: number } {
  const { horizontal, horizontalDir, vertical, verticalDir } = rectProps;
  const dx =
    (cutoutPosition[horizontal] - panelPosition[horizontal]) *
      horizontalDir[horizontal] +
    size.x / 2;
  const dy =
    (-cutoutPosition[vertical] + panelPosition[vertical]) *
      verticalDir[vertical] +
    size.y / 2;
  return { dx, dy };
}

/**
 * Full cutout model in the panel’s 2D plane (same transform as rendering / extrusion).
 *
 * Optional `invert` matches legacy golden fixtures (and imports): mirror in SVG/local space
 * before the standard mirror→rotate→translate used for panel placement.
 */
export function buildCutoutModelInPanelPlane(
  cutout: {
    path?: string;
    position?: { x?: number; y?: number; z?: number };
    orientation?: number;
    invert?: { x?: boolean; y?: boolean };
  },
  size: THREE.Vector2,
  panelPosition: THREE.Vector3,
  side: Sides,
): MakerJS.IModel | null {
  if (!cutout.path || typeof cutout.path !== "string") return null;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) return null;
  const p = cutout.position ?? {};
  const cutoutPosition = {
    x: typeof p.x === "number" ? p.x : 0,
    y: typeof p.y === "number" ? p.y : 0,
    z: typeof p.z === "number" ? p.z : 0,
  };
  const { dx, dy } = computeCutoutDxDy(
    cutoutPosition,
    size,
    panelPosition,
    rectProps,
  );
  let cachedModel = getCachedSvgModel(cutout.path);
  if (cutout.invert?.x) {
    cachedModel = MakerJS.model.mirror(cachedModel, true, false);
  }
  if (cutout.invert?.y) {
    cachedModel = MakerJS.model.mirror(cachedModel, false, true);
  }
  return transformCutoutModel(cachedModel, dx, dy, cutout.orientation || 0);
}

/** Matches SVG path numeric literals without nested quantifier alternation (ReDoS-safe). */
const SVG_PATH_DECIMAL_LITERAL =
  /[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/g;

function roundSvgPathNumbers(path: string): string {
  return path.replaceAll(SVG_PATH_DECIMAL_LITERAL, (match) => {
    const v = Number.parseFloat(match);
    if (!Number.isFinite(v)) return match;
    const rounded = Math.round(v * 1e4) / 1e4;
    return String(rounded);
  });
}

/** Canonical string for comparing two cutouts that may differ in path/position but share the same hole in panel space. */
export function cutoutModelToNormalizedPathString(
  model: MakerJS.IModel,
): string {
  const pathData = MakerJS.exporter.toSVGPathData(model, { origin: [0, 0] });
  const s = typeof pathData === "string" ? pathData : String(pathData ?? "");
  return roundSvgPathNumbers(s.replaceAll(/\s+/g, " ").trim());
}
